<template>
    <div class="settlement-wizard">
        <!-- 스텝 인디케이터 -->
        <div class="stl-steps">
            <span v-for="(s, i) in steps" :key="'s' + i" style="display: contents;">
                <div class="stl-step" :class="{ active: step === i, done: step > i }">
                    <span class="stl-step-no">
                        <i v-if="step > i" class="fa fa-check"></i>
                        <template v-else>{{ i + 1 }}</template>
                    </span>
                    <span>{{ s }}</span>
                </div>
                <div v-if="i < steps.length - 1" class="stl-step-line"></div>
            </span>
        </div>

        <!-- ─── Step 1: 단지·Scope ─────────────────────────── -->
        <div v-show="step === 0" class="stl-section">
            <div class="stl-section-title">단지 기본 정보 · 정산 범위</div>

            <div class="stl-row" style="margin-bottom: 12px;">
                <div class="stl-field">
                    <label>단지명 *</label>
                    <input v-model="form.siteName" type="text" class="stl-input" style="width: 240px;" placeholder="예: 대전 송강동 다세대" />
                </div>
                <div class="stl-field">
                    <label>정산 scope 루트 *</label>
                    <select v-model="form.scopeSysNodeIdx" class="stl-select" style="width: 260px;" @change="loadScopeTree">
                        <option :value="null">선택하세요</option>
                        <option v-for="r in scopeRoots" :key="r.sysNodeIdx" :value="r.sysNodeIdx">
                            {{ r.sysNodeName }} (#{{ r.sysNodeIdx }})
                        </option>
                    </select>
                </div>
            </div>

            <div class="stl-row" style="margin-bottom: 12px;">
                <div class="stl-field">
                    <label>검침일 (1~31)</label>
                    <input v-model.number="form.readingDay" type="number" min="1" max="31" class="stl-input stl-num" style="width: 90px;" />
                </div>
                <div class="stl-field">
                    <label>총 면적 분모 (㎡)</label>
                    <input v-model.number="form.fixedTotalAreaM2" type="number" min="0" step="0.0001" class="stl-input stl-num" style="width: 140px;" />
                </div>
                <div class="stl-field">
                    <label>정산 에너지</label>
                    <div class="stl-radio-group" style="height: 31px;">
                        <label><input v-model="elecEnabledBool" type="checkbox" /> 전기</label>
                        <label><input v-model="gasEnabledBool" type="checkbox" /> 가스</label>
                    </div>
                </div>
            </div>

            <div class="stl-field">
                <label>scope 미리보기</label>
                <ScopeTreePreview :tree="scopeTree" :loading="loadingTree" />
            </div>
        </div>

        <!-- ─── Step 2: 연동 ─────────────────────────────────────────── -->
        <div v-show="step === 1" class="stl-section">
            <div class="stl-section-title">외부 연동</div>

            <div class="stl-row" style="margin-bottom: 12px;">
                <label class="stl-radio-group">
                    <input v-model="kepcoEnabledBool" type="checkbox" :disabled="!elecEnabledBool" />
                    <span style="font-size: 12px; font-weight: 600;">한전 고지 연동 사용</span>
                </label>
            </div>

            <div v-if="elecEnabledBool && kepcoEnabledBool" class="stl-row" style="margin-bottom: 12px;">
                <div class="stl-field">
                    <label>한전 고객번호 (통합 1개)</label>
                    <input v-model="form.kepcoCustNo" type="text" class="stl-input" style="width: 200px;" placeholder="한전 고객번호" />
                </div>
                <div class="stl-field">
                    <label>&nbsp;</label>
                    <button type="button" class="stl-btn" :disabled="!form.kepcoCustNo || testingKepco" @click="testKepco">
                        <b-spinner v-if="testingKepco" small />
                        <i v-else class="fa fa-plug"></i>
                        연결 테스트
                    </button>
                </div>
            </div>
            <div v-if="kepcoTestResult" class="stl-banner" :class="kepcoTestResult.connected ? 'stl-banner-info' : 'stl-banner-warning'">
                <i class="fa" :class="kepcoTestResult.connected ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                <span v-if="kepcoTestResult.connected">
                    연결 확인 — 최근 고지 {{ fmtYm(kepcoTestResult.latestBillYm) }}, 청구액 {{ fmtWon(kepcoTestResult.reqAmt) }}원
                </span>
                <span v-else>고지 데이터 없음 — 고객번호를 확인하세요.</span>
            </div>

            <div class="stl-hint">가스 고지 연동(외부 API)은 2차 범위입니다 — 가스 정산은 세대별 사용량(Nm³) 기준으로 자동 산출됩니다.</div>
        </div>

        <!-- ─── Step 3: 구성·계통 ────────────────────────────────────── -->
        <div v-show="step === 2" class="stl-section">
            <div class="stl-section-title">구성·계통 확인</div>
            <div class="stl-banner stl-banner-info">
                <i class="fa fa-info-circle"></i>
                <span>마법사에서는 미리보기만 제공합니다. 실제 세대 동기화는 [완료] 시 자동 실행됩니다 (단지 생성 → 세대 동기화).</span>
            </div>
            <ScopeTreePreview :tree="scopeTree" :loading="loadingTree" />
        </div>

        <!-- ─── Step 4: 세대 마스터 ──────────────────────────────────── -->
        <div v-show="step === 3" class="stl-section">
            <div class="stl-section-title">세대 마스터 (전용면적 입력 필수)</div>

            <div v-if="missingAreaCount > 0" class="stl-banner stl-banner-warning">
                <i class="fa fa-exclamation-triangle"></i>
                <span>전용면적 미입력 세대 {{ missingAreaCount }}건 — 모두 입력해야 다음으로 진행할 수 있습니다.</span>
            </div>

            <div class="stl-table-container">
                <table class="stl-table">
                    <thead>
                        <tr>
                            <th>호명</th>
                            <th>sysNodeIdx</th>
                            <th class="num">전용면적(㎡) *</th>
                            <th>형별</th>
                            <th>입주</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="h in householdRows" :key="h.sysNodeIdx" :class="{ 'row-warning': !h.exclusiveArea || h.exclusiveArea <= 0 }">
                            <td>{{ h.roomName }}</td>
                            <td class="stl-hint">#{{ h.sysNodeIdx }}</td>
                            <td class="num">
                                <input v-model.number="h.exclusiveArea" type="number" min="0" step="0.01" class="stl-input stl-num" style="width: 110px;" />
                            </td>
                            <td>
                                <input v-model="h.typeName" type="text" class="stl-input" style="width: 110px;" placeholder="예: 59A" />
                            </td>
                            <td>
                                <select v-model="h.isOccupied" class="stl-select">
                                    <option value="Y">입주</option>
                                    <option value="N">공실</option>
                                </select>
                            </td>
                        </tr>
                        <tr v-if="!householdRows.length">
                            <td colspan="5">
                                <div class="stl-empty"><p>scope에 HOUSEHOLD 노드가 없습니다. Step 1에서 scope를 다시 선택하세요.</p></div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ─── 네비게이션 ───────────────────────────────────────────── -->
        <div class="stl-row" style="justify-content: space-between;">
            <button type="button" class="stl-btn" :disabled="step === 0 || submitting" @click="step -= 1">
                <i class="fa fa-chevron-left"></i> 이전
            </button>
            <button v-if="step < steps.length - 1" type="button" class="stl-btn stl-btn-primary" :disabled="!canNext" @click="step += 1">
                다음 <i class="fa fa-chevron-right"></i>
            </button>
            <button v-else type="button" class="stl-btn stl-btn-final" :disabled="!canComplete || submitting" @click="complete">
                <b-spinner v-if="submitting" small />
                <i v-else class="fa fa-check"></i>
                완료 (단지 생성 + 세대 동기화)
            </button>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import ScopeTreePreview from "./components/ScopeTreePreview.vue";
import { fmtWon, fmtYm, parseApiError } from "./utils/settlementFormat.js";

export default {
    name: "SettlementWizard",
    components: { ScopeTreePreview },
    data() {
        return {
            steps: ["단지·범위", "연동", "구성·계통", "세대 마스터"],
            step: 0,
            submitting: false,

            form: {
                siteName: "",
                scopeSysNodeIdx: null,
                readingDay: 30,
                fixedTotalAreaM2: null,
                kepcoCustNo: "",
            },
            kepcoEnabledBool: false,
            elecEnabledBool: true,
            gasEnabledBool: false,
            waterEnabledBool: false,

            scopeRoots: [],
            scopeTree: null,
            loadingTree: false,

            // Step 4 — scope HOUSEHOLD 노드 기반 로컬 편집 (완료 후 bulk 반영)
            householdRows: [],

            testingKepco: false,
            kepcoTestResult: null,
        };
    },
    computed: {
        missingAreaCount() {
            return this.householdRows.filter((h) => !h.exclusiveArea || h.exclusiveArea <= 0).length;
        },
        canNext() {
            if (this.step === 0) return !!this.form.siteName && !!this.form.scopeSysNodeIdx;
            if (this.step === 1) return !this.elecEnabledBool || !this.kepcoEnabledBool || !!this.form.kepcoCustNo;
            if (this.step === 3) return this.householdRows.length > 0 && this.missingAreaCount === 0;
            return true;
        },
        canComplete() {
            return (
                !!this.form.siteName &&
                !!this.form.scopeSysNodeIdx &&
                this.householdRows.length > 0 &&
                this.missingAreaCount === 0
            );
        },
    },
    watch: {
        elecEnabledBool(on) {
            if (!on) this.kepcoEnabledBool = false;
        },
    },
    async mounted() {
        await this.loadScopeRoots();
    },
    methods: {
        fmtWon,
        fmtYm,
        async loadScopeRoots() {
            try {
                const { data } = await backEndApi.settlement.getScopeRoots();
                this.scopeRoots = Array.isArray(data) ? data : [];
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            }
        },
        async loadScopeTree() {
            this.scopeTree = null;
            this.householdRows = [];
            if (!this.form.scopeSysNodeIdx) return;
            this.loadingTree = true;
            try {
                const { data } = await backEndApi.settlement.getSysnodeTree(this.form.scopeSysNodeIdx);
                this.scopeTree = data;
                this.householdRows = (data.nodes || [])
                    .filter((n) => n.role === "HOUSEHOLD")
                    .map((n) => ({
                        sysNodeIdx: n.sysNodeIdx,
                        roomName: n.sysNodeName,
                        exclusiveArea: null,
                        typeName: "",
                        isOccupied: "Y",
                    }));
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.loadingTree = false;
            }
        },
        async testKepco() {
            this.testingKepco = true;
            this.kepcoTestResult = null;
            try {
                const { data } = await backEndApi.settlement.testKepcoConnection({ custNo: this.form.kepcoCustNo });
                this.kepcoTestResult = data;
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.testingKepco = false;
            }
        },
        async complete() {
            this.submitting = true;
            try {
                const areaSum = parseFloat(
                    this.householdRows.reduce((s, r) => s + Number(r.exclusiveArea || 0), 0).toFixed(4),
                );
                const fixedTotalAreaM2 =
                    Number(this.form.fixedTotalAreaM2) > 0 ? Number(this.form.fixedTotalAreaM2) : areaSum || null;

                const payload = {
                    siteName: this.form.siteName,
                    scopeSysNodeIdx: this.form.scopeSysNodeIdx,
                    readingDay: this.form.readingDay,
                    fixedTotalAreaM2,
                    elecEnabled: this.elecEnabledBool ? "Y" : "N",
                    kepcoEnabled: this.elecEnabledBool && this.kepcoEnabledBool ? "Y" : "N",
                    kepcoCustNo: this.elecEnabledBool && this.kepcoEnabledBool ? this.form.kepcoCustNo || null : null,
                    gasEnabled: this.gasEnabledBool ? "Y" : "N",
                    waterEnabled: this.waterEnabledBool ? "Y" : "N",
                };
                const { data: site } = await backEndApi.settlement.createSite(payload);

                // 2. 세대 동기화
                const { data: sync } = await backEndApi.settlement.syncHouseholds(site.siteIdx);

                // 3. Step 4 입력값(면적·형별·공실) 일괄 반영
                const { data: households } = await backEndApi.settlement.getHouseholds(site.siteIdx);
                const bySysNode = new Map((households || []).map((h) => [Number(h.sysNodeIdx), h]));
                const items = this.householdRows
                    .filter((r) => bySysNode.has(Number(r.sysNodeIdx)))
                    .map((r) => ({
                        householdIdx: bySysNode.get(Number(r.sysNodeIdx)).householdIdx,
                        exclusiveArea: r.exclusiveArea,
                        typeName: r.typeName || null,
                        isOccupied: r.isOccupied,
                    }));
                if (items.length) {
                    await backEndApi.settlement.bulkUpdateHouseholds({ items });
                }

                const warnCnt = (sync.warnings || []).length;
                this.alertNoti(
                    `단지 생성 완료 — 세대 동기화: 신규 ${sync.inserted} / 갱신 ${sync.updated} / 건너뜀 ${sync.skipped}` +
                        (warnCnt ? ` / 경고 ${warnCnt}건` : "")
                );
                this.$emit("complete", site);
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
